import React, { useEffect, useReducer } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heading, Loading } from '@puppet/react-components';
import Breadcrumbs from '@components/Breadcrumbs';
import { getRepositories, getSources } from '@utils/api/cd4pe/vcs/actions';
import useWorkspaceDomain from '@hooks/useWorkspaceDomain';
import useWorkspaceName from '@hooks/useWorkspaceName';
import PageLayout from '@components/PageLayout';
import { LINKS } from 'src/routes';
import EditCodeProjectForm, {
  EditCodeProjectFormOnChange,
  EDIT_CODE_PROJECT_FORM_ACTION_TYPES,
  getActualValue,
} from 'src/codeDelivery/components/EditCodeProjectForm';
import { useGetModuleV1 } from '@services/cd4pe/modules';
import Cd4peError from '@components/Cd4peError';
import { defaultEditModuleState, reducer } from './reducer';
import { EditModulePayload, editModuleActions, editModule } from './actions';

const EditModule = () => {
  const [state, dispatch] = useReducer(reducer, defaultEditModuleState);
  const navigate = useNavigate();
  const { t } = useTranslation('codeDelivery');
  const workspaceName = useWorkspaceName();
  const workspaceId = useWorkspaceDomain();
  const { name: moduleDisplayName } = useParams();
  const location = useLocation();

  useEffect(() => {
    if (workspaceId) getSources(workspaceId, dispatch);
  }, [workspaceId]);

  useEffect(() => {
    if (state.saveModuleSuccess) {
      const previousPage = location.state?.from;

      if (previousPage?.includes(moduleDisplayName)) {
        navigate(
          LINKS.codeDelivery.viewModule({
            path: { workspace: workspaceName, name: moduleDisplayName },
          }),
          {
            state: {
              alertMessage: t('editCodeProject.form.successMessage.module', {
                moduleName: moduleDisplayName,
              }),
            },
          },
        );
      } else {
        navigate(
          LINKS.codeDelivery.listModules({
            path: { workspace: workspaceName },
          }),
          {
            state: {
              alertMessage: t('editCodeProject.form.successMessage.module', {
                moduleName: moduleDisplayName,
              }),
            },
          },
        );
      }
    }
  }, [
    workspaceName,
    state.saveModuleSuccess,
    navigate,
    location.state?.from,
    moduleDisplayName,
    t,
  ]);

  const {
    data,
    isLoading: currentModuleLoading,
    error: currentModuleError,
  } = useGetModuleV1({
    workspaceId,
    moduleName: moduleDisplayName!,
  });

  const srcRepoName = data?.srcRepoName;

  const matchedRepo =
    getActualValue(state.repositories, srcRepoName!) ||
    getActualValue(state.repositories, moduleDisplayName!);

  const getSourceDisplayName = (sourceActualValue: string) => {
    return state.sources
      .map((source) => {
        if (source.actualValue.id === sourceActualValue) {
          return source.actualValue.displayName;
        }
        return null;
      })
      .join('');
  };

  const matchingError =
    !matchedRepo && state.selectedSource && !state.getReposLoading
      ? t('editCodeProject.form.errorMessage.module', {
          moduleName: srcRepoName,
          displayName: getSourceDisplayName(state.selectedSource),
        })
      : null;

  let saveErrMsg: string | null = null;
  if (state.saveModuleError) {
    saveErrMsg =
      typeof state.saveModuleError === 'string'
        ? state.saveModuleError
        : state.saveModuleError.message;
  }

  const breadcrumbs = [
    { displayName: `${workspaceName}` },
    {
      displayName: t('newModule.breadcrumb.modules'),
      linkDestination: LINKS.codeDelivery.listModules({
        path: { workspace: workspaceName },
      }),
    },
    {
      displayName: `${moduleDisplayName}`,
      linkDestination: LINKS.codeDelivery.viewModule({
        path: { workspace: workspaceName, name: moduleDisplayName },
      }),
    },
    { displayName: 'Edit' },
  ];

  const onFormChange: EditCodeProjectFormOnChange = (fieldName, values) => {
    dispatch({ type: fieldName, payload: values[fieldName] || '' });

    switch (fieldName) {
      case EDIT_CODE_PROJECT_FORM_ACTION_TYPES.SELECTED_SOURCE: {
        const selectedSource = getActualValue(
          state.sources,
          values.SELECTED_SOURCE || '',
        );
        if (selectedSource) {
          getRepositories(workspaceId, dispatch, selectedSource, srcRepoName);
        }
        break;
      }
      default:
    }
  };

  const onClickEditModule = async () => {
    dispatch(editModuleActions.editModuleStart());

    const modulePayload: EditModulePayload = {
      workspaceId,
      moduleName: moduleDisplayName!,
      requestBody: {
        srcRepoProvider: matchedRepo!.provider,
        srcRepoDisplayOwner: matchedRepo!.ownerDisplayName || '',
        srcRepoOwner:
          matchedRepo!.organization ||
          matchedRepo!.user ||
          matchedRepo!.project ||
          '',
        srcRepoId:
          matchedRepo!.project ||
          matchedRepo!.user ||
          matchedRepo!.organization ||
          '',
      },
    };

    editModule(dispatch, modulePayload);
  };

  const onClickCancel = () => {
    navigate(
      LINKS.codeDelivery.listModules({
        path: { workspace: workspaceName },
      }),
    );
  };

  if (currentModuleLoading) {
    return <Loading />;
  }

  if (currentModuleError) {
    return <Cd4peError error={currentModuleError} />;
  }

  return (
    <PageLayout data-testid="edit-module-view" className="edit-module-view">
      <PageLayout.Header>
        <PageLayout.Breadcrumbs>
          <Breadcrumbs breadcrumbs={breadcrumbs} />
        </PageLayout.Breadcrumbs>
        <Heading>Edit {moduleDisplayName}</Heading>
      </PageLayout.Header>
      <PageLayout.Content>
        <EditCodeProjectForm
          workspaceName={workspaceName}
          projectType="module"
          sources={state.sources}
          selectedSource={state.selectedSource}
          getSourcesLoading={state.getSourcesLoading}
          getSourcesError={state.getSourcesError}
          getReposLoading={state.getReposLoading}
          getReposError={state.getReposError || matchingError}
          name={srcRepoName}
          saveLoading={state.saveModuleLoading}
          saveError={saveErrMsg}
          webhookError={state.saveModuleWebhookError}
          onFormChange={onFormChange}
          submitCallback={onClickEditModule}
          submitDisabled={matchedRepo === null}
          cancelCallback={onClickCancel}
          closeModal={onClickCancel}
        />
      </PageLayout.Content>
    </PageLayout>
  );
};

export default EditModule;
